import { Flexbox, Form } from '@/components'
import yup from '@/lib/yupPt'
import clinicalCarePriorities from '@/services/api/clinicalCarePriorities'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import ReactTextareaAutosize from 'react-textarea-autosize'
import { toast } from 'react-toastify'

const schema = yup.object().shape({
  title: yup.string().trim().required(),
  description: yup.string().trim().required().min(5),
  level: yup.number().min(0).required(),
  color: yup.string().trim().required(),
})

function FormPriority({
  btnClass, btnIcon, btnText, priority, onApiResponse,
}) {
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)

  const onSave = useCallback(async (values) => {
    const params = priority ? [priority.id, values] : [values]
    const method = priority ? clinicalCarePriorities.update : clinicalCarePriorities.create
    try {
      setLoading(true)
      const { data } = await method(...params)
      toast.success(`Prioridade ${priority ? 'atualizada' : 'criada'} com sucesso`)
      onApiResponse(data)
      setLoading(false)
      setActive(false)
    } catch (e) {
      setLoading(false)
      toast.error(e.message)
    }
  }, [onApiResponse, priority])

  return (
    <>
      <button
        type="button"
        className={btnClass}
        onClick={() => setActive(true)}
      >
        <i className={btnIcon} />
        {btnText ? ` ${btnText}` : ''}
      </button>
      {
        active && (

          <fieldset disabled={loading}>
            <Form.Formik
              initialValues={{
                title: priority?.title ?? '',
                description: priority?.description ?? '',
                color: priority?.color ?? '',
                level: priority?.level ?? '',
              }}
              onSubmit={onSave}
              validationSchema={schema}
              validateOnMount
            >
              {
                ({ touched, errors, isValid }) => (
                  <Form.ValidationForm>
                    <div className={`modal ${active ? 'active' : ''} text-left`}>
                      <a type="button" className="modal-overlay" aria-label="Close" onClick={() => setActive(false)} />
                      <div className="modal-container">
                        <div className="modal-header">
                          <a
                            type="button"
                            className="btn btn-clear float-right"
                            onClick={() => setActive(false)}
                          />
                          <div className="modal-title h5">
                            {priority ? 'Editar prioridade' : 'Nova prioridade'}
                          </div>
                        </div>
                        <div className="modal-body">
                          <Flexbox.Columns>
                            <Flexbox.Column className="col-12">
                              <Form.Group className={`${touched?.title && errors?.title ? 'has-error' : ''}`}>
                                <Form.Label htmlFor="title">Título <span className="text-error">*</span></Form.Label>
                                <Form.ValidationField
                                  name="title"
                                  id="title"
                                  placeholder="Título da prioridade"
                                />
                                {
                                  touched?.title && errors?.title
                                    ? <Form.Hint className="text-error">{errors.title}</Form.Hint> : null
                                }
                              </Form.Group>
                            </Flexbox.Column>
                            <Flexbox.Column className="col-12">
                              <Form.Group className={`${touched?.description && errors?.description ? 'has-error' : ''}`}>
                                <Form.Label htmlFor="description">Observações <span className="text-error">*</span></Form.Label>
                                <Form.ValidationField
                                  name="description"
                                  id="description"
                                >
                                  {
                                    ({ field }) => (
                                      <ReactTextareaAutosize
                                        name="description"
                                        style={{ resize: 'none' }}
                                        className="form-input"
                                        rows={1}
                                        maxRows={3}
                                        placeholder="Descreva exemplos ou observações sobre essa prioridade"
                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                        {...field}
                                      />
                                    )
                                  }
                                </Form.ValidationField>
                                {
                                  touched?.description && errors?.description
                                    ? <Form.Hint className="text-error">{errors.description}</Form.Hint> : null
                                }
                              </Form.Group>
                            </Flexbox.Column>
                            <Flexbox.Column className="col-6 col-sm-12">
                              <Form.Group className={`${touched?.color && errors?.color ? 'has-error' : ''}`}>
                                <Form.Label htmlFor="color">Cor <span className="text-error">*</span></Form.Label>
                                <Form.ValidationField
                                  type="color"
                                  name="color"
                                  id="color"
                                  placeholder="Título da prioridade"
                                />
                                {
                                  touched?.color && errors?.color
                                    ? <Form.Hint className="text-error">{errors.color}</Form.Hint> : null
                                }
                              </Form.Group>
                            </Flexbox.Column>
                            <Flexbox.Column className="col-6 col-sm-12">
                              <Form.Group className={`${touched?.level && errors?.level ? 'has-error' : ''}`}>
                                <Form.Label htmlFor="color">Nível <span className="text-error">*</span></Form.Label>
                                <Form.ValidationField
                                  type="number"
                                  step="1"
                                  name="level"
                                  id="level"
                                  min="0"
                                  placeholder="Nível da prioridade"
                                />
                                {
                                  touched?.level && errors?.level
                                    ? <Form.Hint className="text-error">{errors.level}</Form.Hint> : null
                                }
                                <Form.Hint>Quanto menor mais prioritário</Form.Hint>
                              </Form.Group>
                            </Flexbox.Column>
                          </Flexbox.Columns>
                        </div>
                        <div className="modal-footer">
                          <button type="submit" disabled={!isValid} className={`btn btn-primary ${loading ? 'loading' : ''}`}>
                            <i className="fas fa-save" />
                            &nbsp;Salvar
                          </button>
                        </div>
                      </div>
                    </div>
                  </Form.ValidationForm>
                )
              }
            </Form.Formik>
          </fieldset>
        )
      }
    </>
  )
}

FormPriority.propTypes = {
  btnClass: PropTypes.string.isRequired,
  btnIcon: PropTypes.string.isRequired,
  onApiResponse: PropTypes.func.isRequired,
  priority: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    level: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  btnText: PropTypes.string,
}

FormPriority.defaultProps = {
  priority: null,
  btnText: '',
}

export default FormPriority
